import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  NgZone,
  TemplateRef,
  input,
  output,
  computed,
  signal,
  viewChild,
  OnInit
} from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { Column, TableCellClickEvent, TableScrollEvent } from './models/table.models'
import { defaultTrackBy } from './utils/table.utils'
import { DEFAULT_STICKY_ROW_STYLE } from './constants/table.constants'
import { throttle } from '../../decorators/throttle.decorator'
import { SortType } from './enums/table.enums'
import { get, partition } from 'lodash-es'
import { LoaderComponent } from '../loader/loader.component'
import { GridTemplateDirective } from './directives/grid-template.directive'
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common'
import { TranslatePipe } from '@ngx-translate/core'

@Component({
  selector: 'mcm-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoaderComponent, GridTemplateDirective, NgClass, NgStyle, NgTemplateOutlet, TranslatePipe]
})
export class TableComponent<T = unknown> implements OnInit {
  private readonly _zn: NgZone = inject(NgZone)
  private _lastScroll = 0

  readonly rows = input<T[]>([])
  readonly columns = input<Column[]>([])
  readonly sortable = input<boolean>(false)
  readonly headerHeight = input<string | undefined>(undefined)
  readonly rowHeight = input<string | undefined>(undefined)
  readonly drawRowHolder = input<boolean>(false)
  readonly loading = input<boolean>(false)
  readonly stickyHeader = input(true)
  readonly cellTpl = input<TemplateRef<unknown> | undefined>(undefined)
  readonly headerTpl = input<TemplateRef<unknown> | undefined>(undefined)
  readonly rowStyles = input<Partial<CSSStyleDeclaration> | undefined>(undefined)
  readonly excludeFromSortingFn = input<((row: T, ...args: unknown[]) => boolean) | undefined>(undefined)
  readonly trackByFn = input<(...args: any[]) => unknown>(defaultTrackBy)

  readonly cellClick = output<TableCellClickEvent<T>>()
  readonly tableScroll = output<TableScrollEvent>()

  readonly scrollable = viewChild<ElementRef<HTMLElement>>('scrollable')

  readonly ready$ = toObservable(this.scrollable)
  private readonly _sortState = signal<{ index: number | null; direction: 'asc' | 'desc' | 'def' }>({
    index: null,
    direction: 'def'
  })

  defaultRowStyles: Partial<CSSStyleDeclaration> = DEFAULT_STICKY_ROW_STYLE

  readonly displayedColumns = computed(() => {
    const cols = this.columns() || []
    const sort = this._sortState()

    return cols.map((c, i) => ({
      ...c,
      sortDirection: sort.index === i ? sort.direction : c.sortDirection === 'def' ? 'def' : undefined
    }))
  })

  readonly displayedRows = computed(() => {
    const baseRows = this.rows() || []
    const cols = this.columns() || []
    const sort = this._sortState()

    if (!baseRows.length || sort.index === null || sort.direction === 'def') {
      return baseRows
    }

    const column = cols[sort.index]
    let sortedRows =
      this._getSortType(column) === SortType.External
        ? this._externalSort(baseRows, column, sort.direction)
        : this._buildInSort(baseRows, column, sort.direction)

    const excludeFn = this.excludeFromSortingFn()
    if (excludeFn) {
      const [excluded, included] = partition(sortedRows, (row) => excludeFn(row))
      sortedRows = [...included, ...excluded]
    }

    return sortedRows
  })

  ngOnInit(): void {
    const initialSortIndex = this.columns()?.findIndex((c) => c.sortDirection && c.sortDirection !== 'def')
    if (initialSortIndex !== undefined && initialSortIndex >= 0) {
      this._sortState.set({
        index: initialSortIndex,
        direction: this.columns()[initialSortIndex].sortDirection as 'asc' | 'desc' | 'def'
      })
    }
  }

  sort(index: number): void {
    const cols = this.columns() || []
    if (!cols[index]) return

    this._sortState.update((state) => {
      const isSameColumn = state.index === index
      const currentDirection = isSameColumn && state.direction ? state.direction : cols[index].sortDirection || 'def'

      const newDirection = currentDirection === 'def' ? 'desc' : currentDirection === 'desc' ? 'asc' : 'def'

      return { index, direction: newDirection }
    })
  }

  private _getSortType(column: Column): SortType {
    if (column.sortFn) return SortType.External
    if (this.sortable() && column.sortable !== false) return SortType.BuiltIn
    return SortType.None
  }

  private _getSortKey(column: Column): string {
    return column.sortableKey || column.key || column.label
  }

  private _externalSort(rows: T[], column: Column, direction: 'asc' | 'desc' | 'def'): T[] {
    return [...rows].sort((a, b) =>
      column.sortFn!(a, b, direction, column.sortableKey!, column.sortableKeyFallback!, column.key, column.label)
    )
  }

  private _buildInSort(rows: T[], column: Column, direction: 'asc' | 'desc' | 'def'): T[] {
    const key = this._getSortKey(column)
    return [...rows].sort((a, b) => {
      const _a = +get(a, key, 0)
      const _b = +get(b, key, 0)
      return direction === 'desc' ? _b - _a : _a - _b
    })
  }

  @throttle(100)
  onScroll(ev: Event): void {
    ev.preventDefault()
    ev.stopPropagation()

    this._zn.runOutsideAngular(() => {
      const target = ev.target as HTMLElement
      const scroll = Math.max(0, target.scrollTop)
      const direction = this._lastScroll < scroll ? 'down' : 'up'
      this._lastScroll = scroll

      this._zn.run(() => {
        this.tableScroll.emit({ event: ev, direction, isTopReached: target.scrollTop <= 0 })
      })
    })
  }
}
